import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createMobileTemplate,
  mobileTemplatesListQuery,
  mobileTemplatesShellQuery,
  removeMobileTemplate,
  updateMobileTemplate,
  type MobileTemplateRow,
} from '../../api/settingsEaseedeskMobileTemplates'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { LoadFailed } from '../../ui/LoadFailed'
import { Spinner } from '../../ui/Spinner'
import { TemplateModal, bodyExcerpt, updatedLabel } from './EmailTemplatesPage'

/**
 * Settings → Easeedesk → Mobile templates.
 *
 * Element cu element fata de `version-3/easeedesk_settings/notifications/
 * mobile_templates/index.php` + `list.php`. Fata de sabloanele de email difera
 * doar prin doua lucruri, si amandoua sunt in macheta:
 *
 *  - NU ARE CASUTA DE CAUTARE. `mobile_templates/index.php` n-are `#myInput` si
 *    nici `searchTemplate()` in `index-1.js`.
 *  - simbolul campului propriu se facea din ETICHETA campului, nu din nume -
 *    si de-aia nu se inlocuia niciodata la trimitere. Vezi
 *    `EaseedeskMobileTemplates` in api: acolo e scris ce s-a indreptat.
 *
 * Fereastra de scriere e aceeasi (`TemplateModal`), cu alt cap de salvare.
 */

/** Capetele ecranului asta, date ferestrei comune. */
function saveMobileTemplate(template: MobileTemplateRow | null, name: string, subject: string, body: string) {
  return template === null
    ? createMobileTemplate(name, subject, body)
    : updateMobileTemplate(template.template_id, name, subject, body)
}

export function MobileTemplatesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(mobileTemplatesShellQuery())
  const list = useQuery(mobileTemplatesListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<MobileTemplateRow | null>(null)
  const [preview, setPreview] = useState<MobileTemplateRow | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'mobile_templates'] })
  }

  const remove = useMutation({
    mutationFn: (template: number) => removeMobileTemplate(template),
    onSuccess: () => {
      toast('Template removed.', 'Success')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="The mobile templates" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const templates = list.data?.templates ?? []

  return (
    <div>
      <h4>
        <strong>Mobile</strong> templates
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit ticket <strong>types</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create new template
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed what="The templates" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : templates.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No mobile templates.
          </div>
        ) : (
          templates.map((template) => (
            <section className="settings" key={template.template_id}>
              <div className="row">
                <div className="col-md-2">
                  <h5>
                    <i className="far fa-envelope-open" aria-hidden="true"></i> {template.template_name}
                  </h5>
                  <p className="color-green3" style={{ marginTop: 5 }}>
                    <i className="fas fa-circle" aria-hidden="true"></i> Active
                  </p>
                </div>
                <div className="col-md-2">
                  <p>{template.template_subject}</p>
                  <p className="color-blue" style={{ marginTop: 5 }}>
                    Subject
                  </p>
                </div>
                <div className="col-md-4" style={{ wordWrap: 'break-word' }}>
                  <div>{bodyExcerpt(template.template_body)}</div>
                  <a
                    href="#"
                    className="preview-template color-blue"
                    onClick={(e) => {
                      e.preventDefault()
                      setPreview(template)
                    }}
                  >
                    <i
                      className="fas fa-search-plus"
                      aria-hidden="true"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="Preview template"
                    ></i>{' '}
                    Preview message body
                  </a>
                </div>
                <div className="col-md-2">
                  <span className="updated">{updatedLabel(template.updated)}</span>
                  <p className="color-green3">
                    <i className="far fa-clock" aria-hidden="true"></i> Updated
                  </p>
                </div>
                <div className="col-md-2 aright">
                  <button type="button" className="btn btn-xs btn-primary" onClick={() => setEdited(template)}>
                    Edit
                  </button>{' '}
                  <button
                    type="button"
                    className="btn btn-xs btn-danger remove-template"
                    onClick={() => {
                      if (!window.confirm('Are you sure?')) return
                      remove.mutate(template.template_id)
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <hr className="dotted" />
            </section>
          ))
        )}
      </div>

      <Modal
        id="previewModal"
        labelledBy="previewModalLabel"
        open={preview !== null}
        onClose={() => setPreview(null)}
        title="Message body"
      >
        <div dangerouslySetInnerHTML={{ __html: preview?.template_body ?? '' }} />
      </Modal>

      <TemplateModal
        open={creating}
        template={null}
        options={shell.data.options}
        save={saveMobileTemplate}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
      <TemplateModal
        open={edited !== null}
        template={edited}
        options={shell.data.options}
        save={saveMobileTemplate}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}
